import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  styled,
  Switch,
  Stack,
  FormGroup,
  Snackbar,
} from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { setPassword } from "../../../Actions/UpdatePasswordAction";
import "./profileUpdatepass.scss";
import "../../../assets/styles/checkboxRadio.scss";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,
  display: "flex",
  borderRadius: 16,
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(20px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "rgba(8, 109, 171, 1)",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    borderRadius: 16,
  },
  "& .MuiSwitch-track": {
    borderRadius: 8,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
  },
}));

const UpdatePassword = () => {
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordBtnDisable, setPasswordBtnDisable] = useState(false)
  const [classForToast, setClassForToast] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    mix: false,
    special: false,
  });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [mfa, setMfa] = useState(false);

  const dispatch = useDispatch();
  const { successMessage, errorMessage } = useSelector(
    (state) => state.setPasswordData
  );
  const enableSnackbar = useSelector((state) => state.snackbar.enableSnackbar);
  const testModeEnabled = useSelector(
    (state) => state.snackbar.testModeEnabled
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setPasswordBtnDisable(true)
    if (name === "newPassword") {
      setPasswordStrength({
        length: value.length >= 8,
        mix: /[a-z]/.test(value) && /[A-Z]/.test(value),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(value),
      });
    }
  };

  const handleValidation = () => {
    if (!formData.newPassword) {
      setToast("Please enter your new password.", "error");
      return false;
    }
    if (
      !passwordStrength.length ||
      !passwordStrength.mix ||
      !passwordStrength.special
    ) {
      setToast("Password does not meet strength requirements.", "error");
      return false;
    }
    if (!formData.confirmNewPassword) {
      setToast("Please confirm your new password.", "error");
      return false;
    }
    if (formData.confirmNewPassword !== formData.newPassword) {
      setToast("Passwords do not match.", "error");
      return false;
    }
    return true;
  };

  const setToast = (msg, type) => {
    setMessage(msg);
    setClassForToast(type);
    setOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (handleValidation()) {
      dispatch(setPassword(formData));
    }
  };

  useEffect(() => {
    if (successMessage) {
      setToast(successMessage, "success");
      setFormData({ newPassword: "", confirmNewPassword: "" });
    } else if (errorMessage) {
      setToast(errorMessage, "error");
    }
  }, [successMessage, errorMessage]);

  const handleClose = () => {
    setOpen(false);
    setMessage("");
    setClassForToast("");
  };

  return (
    <div className="update_pass_mnWrapper">
      <div className="updatePassword_myacc">
        <div className="update_wrapper">
          <div className="profile-form">
            <h2>Update Password</h2>
            <Box
              component="form"
              noValidate
              autoComplete="off"
              className="field"
              aria-describedby="password-guidelines-profile"
            >
              <TextField
                id="newPassword"
                name="newPassword"
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                variant="outlined"
                value={formData.newPassword}
                onChange={handleInputChange}
                slotProps={{
                  input: {
                    style: { borderRadius: "4px" },
                  },
                  inputLabel: {
                    shrink: true, // important!
                    style: { transform: "inherit" },
                  },

                  inputAdornment: {
                    position: "end",
                  },
                  endAdornment: {
                    children: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                id="confirmNewPassword"
                name="confirmNewPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                value={formData.confirmNewPassword}
                onChange={handleInputChange}
                slotProps={{
                  input: {
                    style: { borderRadius: "4px" },
                  },
                  inputLabel: {
                    shrink: true, // important!
                    style: { transform: "inherit" },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <div
              className="password-strength"
              id="password-guidelines-profile"
              aria-live="polite"
            >
              <p>Password strength:</p>
              <ul
                style={{
                  paddingLeft: 0,
                  marginTop: "5px",
                  listStyleType: "none",
                }}
              >
                <li aria-live="polite" aria-atomic="true">
                  <div
                    className={passwordStrength.length ? "valid" : "invalid"}
                  >
                    {passwordStrength.length ? (
                      <CheckCircleOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment met"
                      />
                    ) : (
                      <ErrorOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment not met"
                      />
                    )}{" "}
                    At least 8 characters
                  </div>
                </li>
                <li aria-live="polite" aria-atomic="true">
                  <div className={passwordStrength.mix ? "valid" : "invalid"}>
                    {passwordStrength.mix ? (
                      <CheckCircleOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment met"
                      />
                    ) : (
                      <ErrorOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment not met"
                      />
                    )}{" "}
                    Upper and lowercase letters
                  </div>
                </li>
                <li aria-live="polite" aria-atomic="true">
                  <div
                    className={passwordStrength.special ? "valid" : "invalid"}
                  >
                    {passwordStrength.special ? (
                      <CheckCircleOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment met"
                      />
                    ) : (
                      <ErrorOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment not met"
                      />
                    )}{" "}
                    Special character
                  </div>
                </li>
              </ul>
            </div>

            <div className="profile_wrapperbtn">
              {/* <button
                type="button"
                className="btnSubmit cancel"
                onClick={() =>
                  setFormData({ newPassword: "", confirmNewPassword: "" })
                }
              >
                Cancel
              </button> */}
              <button
                type="submit"
                className="btnSubmit save containedButton"
                onClick={handleSubmit}
                data-testid="save-password-button"
                disabled={!passwordBtnDisable}
              >
                Save
              </button>
            </div>
          </div>
        </div>

        <div className="update_wrapper security_check_wrapper" style={{display: 'none'}}>
          <div className="profile-form">
            <h2>Security Settings</h2>
            <div className="securityChecks gl_toggle">
              <div className="sc_heading">
                <p>Enable Multi Factor Authentication</p>
                <span>
                  If enabled, the user will have to go through the multi factor
                  authentication when logging in.
                </span>
              </div>
              <div className="sc_toggle">
                <FormGroup>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <AntSwitch
                      disabled
                      checked={mfa}
                      onChange={() => setMfa(!mfa)}
                      slotProps={{
                        input: {
                          "aria-label": "Enable Multi Factor Authentication",
                        },
                      }}
                    />
                  </Stack>
                </FormGroup>
              </div>
            </div>
            <div className="profile_wrapperbtn">
              <button
                type="submit"
                className="btnSubmit save containdBtndisbled containedButton"
                disabled
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={open}
        className={classForToast === "error" ? "error" : "success"}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        message={message}
        onClose={handleClose}
        sx={{
          pointerEvents: "none",
          ...(enableSnackbar || testModeEnabled ? { paddingTop: "45px" } : {}),
        }}
      />

      {/* <ActivityLog /> */}
    </div>
  );
};

export default UpdatePassword;
